import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  LinearProgress,
  Typography,
  Stack,
  Alert
} from '@mui/material';
import { capitalise } from '../../utils/stringUtil';
import { isPaynowReturn } from '../../utils/paymentUtils';
import paynowService from '../../utils/paynowService';
import { isNative } from '../../utils/platform';

interface PaynowPaymentFormProps {
  amount: number;
  purpose: string;
  propertyId: string;
  reference: string | null;
  redirectUrl: string | null;
  pollUrl?: string | null;
  onSuccess: (result?: {
    topUpStatus?: string;
    topUpError?: string;
    meterProvider?: string;
    meterId?: string;
    metaInfo?: unknown;
  }) => void;
}

const PaynowPaymentForm: React.FC<PaynowPaymentFormProps> = ({
  amount,
  purpose,
  reference,
  redirectUrl,
  pollUrl,
  onSuccess
}) => {
  const [checkingStatus, setCheckingStatus] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [paymentStatus, setPaymentStatus] = useState<'pending' | 'success' | 'failed'>('pending');
  const [progress, setProgress] = useState(50);
  const [popupBlocked, setPopupBlocked] = useState(false);
  const [topUpFeedback, setTopUpFeedback] = useState<{
    topUpStatus?: string;
    topUpError?: string;
    meterProvider?: string;
    meterId?: string;
    metaInfo?: unknown;
  } | null>(null);
  const pollIntervalRef = React.useRef<any>(null);
  // Helper function to handle payment window opening
  const openPaymentWindow = (url: string) => {
    if (isNative()) {
      window.location.assign(url);
      return true;
    }

    const paymentWindow = window.open(url, '_blank');

    // Show a message if popup was blocked
    if (!paymentWindow || paymentWindow.closed || typeof paymentWindow.closed === 'undefined') {
      console.warn('Payment popup may have been blocked. Please click the link to open the payment page.');
      setPopupBlocked(true);
      return false;
    } else {
      setPopupBlocked(false);
      return true;
    }
  };

  // Unified function to check payment status using PaynowService
  const checkPaymentStatusInternal = async (ref: string, showLoading = false) => {
    if (showLoading) {
      setCheckingStatus(true);
    }

    try {
      const statusResponse = await paynowService.checkPaymentStatus(ref);

      if (!statusResponse) {
        if (showLoading) {
          setError('Error checking payment status. Please try again.');
        }
        return false;
      }

      console.log(`Payment status check response${!showLoading ? ' (auto)' : ''}:`, statusResponse);

      if (statusResponse.paid || statusResponse.status === 'paid') {
        const feedback = {
          topUpStatus: statusResponse.topUpStatus,
          topUpError: statusResponse.topUpError,
          meterProvider: statusResponse.meterProvider,
          meterId: statusResponse.meterId,
          metaInfo: statusResponse.metaInfo,
        };
        setTopUpFeedback(feedback);
        setPaymentStatus('success');
        setProgress(100);
        onSuccess(feedback);
        return true;
      } else if (statusResponse.status === 'cancelled' || statusResponse.status === 'failed') {
        setPaymentStatus('failed');
        setError('Payment was cancelled or failed');
        return false;
      }
      return false;
    } catch (err) {
      console.error(`Error ${showLoading ? 'checking' : 'polling'} payment status:`, err);
      if (showLoading) {
        setError('Error checking payment status. Please try again.');
      }
      return false;
    } finally {
      if (showLoading) {
        setCheckingStatus(false);
      }
    }
  };

  // Public function to check payment status with loading indicator
  const checkPaymentStatus = (ref: string) => checkPaymentStatusInternal(ref, true);

  // Start polling for payment status
  const startPolling = (ref: string) => {
    // Clear any existing interval
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
    }

    // Poll every 5 seconds to check payment status
    pollIntervalRef.current = setInterval(async () => {
      const success = await checkPaymentStatusInternal(ref, false);
      if (success) {
        clearInterval(pollIntervalRef.current);
      }
    }, 5000);

    // Stop polling after 10 minutes (600000 ms) if no response
    setTimeout(() => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
      if (paymentStatus === 'pending') {
        setPaymentStatus('failed');
        setError('Payment timed out. Please try again.');
      }
    }, 600000);
  };

  // Auto-open the payment window as soon as a redirectUrl is available
  useEffect(() => {
    if (redirectUrl && !isPaynowReturn()) {
      openPaymentWindow(redirectUrl);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [redirectUrl]);

  // Initialize component state based on props
  useEffect(() => {
    // Check if this is a return from PayNow payment
    if (isPaynowReturn()) {
      // The PaymentReturnHandler component will handle the verification
      // We just need to show a loading state until it completes
      setPaymentStatus('pending');
      setProgress(70);
      return;
    }

    // If we have a reference, start checking payment status
    if (reference) {
      startPolling(reference);
    }

    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reference]);

  // Helper component for progress bar
  const PaymentProgressBar = ({ value, color = "primary" }: { value: number, color?: "primary" | "secondary" | "error" | "info" | "success" | "warning" }) => (
    <LinearProgress
      variant="determinate"
      value={value}
      color={color}
      style={{ marginTop: '1rem', height: 10 }}
    />
  );

  const renderPendingStatus = () => (
    <Box sx={{ mt: 2, p: 3 }}>
      <Alert severity="info">
        Please complete your payment in the PayNow window that opened. This page will update automatically when your payment is confirmed.
      </Alert>

      {popupBlocked && redirectUrl && (
        <Alert severity="warning" sx={{ mt: 2 }}>
          <Typography>
            The payment window may have been blocked by your browser.
            <Button
              component="a"
              href={redirectUrl}
              target="_blank"
              rel="noopener noreferrer"
              color="primary"
              sx={{ ml: 1 }}
            >
              Click here to open the payment page
            </Button>
          </Typography>
        </Alert>
      )}

      <PaymentProgressBar value={progress} />
      <Typography sx={{ mt: 2 }}>Waiting for payment confirmation...</Typography>

      <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
        {redirectUrl && (
          <Button
            variant="outlined"
            color="primary"
            onClick={() => openPaymentWindow(redirectUrl)}
          >
            Reopen Payment Window
          </Button>
        )}

        {reference && (
          <Button
            variant="outlined"
            color="secondary"
            onClick={() => checkPaymentStatus(reference)}
            disabled={checkingStatus}
            startIcon={checkingStatus ? <CircularProgress size={20} /> : null}
          >
            {checkingStatus ? 'Checking...' : 'Check Payment Status'}
          </Button>
        )}
      </Box>
    </Box>
  );

  const renderSuccessStatus = () => (
    <Box sx={{ mt: 2, p: 3 }}>
      <Alert severity="success">
        Payment successful! Your transaction has been completed.
      </Alert>
      {topUpFeedback?.topUpError && (
        <Alert severity="warning" sx={{ mt: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            Meter top-up needs admin attention.
          </Typography>
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
            {topUpFeedback.topUpError}
          </Typography>
          {(topUpFeedback.meterProvider || topUpFeedback.meterId || topUpFeedback.topUpStatus) && (
            <Typography variant="body2" sx={{ mt: 1 }}>
              Provider: {topUpFeedback.meterProvider || 'unknown'} | Meter: {topUpFeedback.meterId || 'unknown'} | Status: {topUpFeedback.topUpStatus || 'unknown'}
            </Typography>
          )}
        </Alert>
      )}
      <PaymentProgressBar value={100} color="success" />
    </Box>
  );

  const renderFailedStatus = () => (
    <Box sx={{ mt: 2, p: 3 }}>
      <Alert severity="error">
        {error || 'Payment failed. Please try again.'}
      </Alert>
    </Box>
  );

  // Render the appropriate content based on payment status
  const renderContent = () => {
    switch (paymentStatus) {
      case 'pending':
        return renderPendingStatus();
      case 'success':
        return renderSuccessStatus();
      case 'failed':
        return renderFailedStatus();
      default:
        return renderPendingStatus();
    }
  };

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        Pay with PayNow for {capitalise(purpose)}
      </Typography>
      {renderContent()}
    </Box>
  );
};

export default PaynowPaymentForm;
